import { formatEur, formatRange, computeSimilarity, humanizeCohortName } from './networkUtils'

/**
 * Generate a print-ready Negotiation Data Pack (open in a new tab → Save as PDF).
 * Ported from the supply-chain demo, extended with a Site Price Gap variant.
 * Reads everything from the opportunity object (opp.parts carry full part detail).
 */

const OPP_TYPES = {
  negotiation: { label: 'Negotiation', icon: '💰', color: '#fef2f2', text: '#dc2626' },
  sole_source: { label: 'Sole-Source Risk', icon: '⚠️', color: '#fffbeb', text: '#d97706' },
  part_family: { label: 'Part Family', icon: '🔗', color: '#eef2ff', text: '#6366f1' },
  quick_win: { label: 'Quick Win', icon: '⚡', color: '#ecfdf5', text: '#059669' },
  site_gap: { label: 'Site Price Gap', icon: '🌍', color: '#ecfeff', text: '#0891b2' },
}

const esc = (v) => String(v == null ? '' : v).replace(/[&<>"]/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
))

export function generateDataPack(opp) {
  const OT = OPP_TYPES[opp.type] || OPP_TYPES.negotiation
  const nt = opp._negotiationTarget || 1.1
  const today = new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })
  const fullParts = opp.parts

  // ── Build detailed line items ──
  const lineItems = []
  if (opp.type === 'site_gap') {
    const best = opp.vizData?.best || 0
    ;(opp.vizData?.sites || []).forEach(s => {
      const over = best > 0 ? (s.unit_cost / best - 1) : 0
      lineItems.push({
        site: s.site, suppliers: (s.suppliers || []).join(', '),
        uc: Number(s.unit_cost).toFixed(2), delta: (over * 100).toFixed(0),
        spend: s.spend, savings: s.spend * (best > 0 ? (1 - best / s.unit_cost) : 0),
        isBest: s.unit_cost <= best,
      })
    })
  } else {
    fullParts.forEach(p => {
      (p.suppliers || []).forEach(s => {
        if (opp.type === 'negotiation') {
          if (s.price_position && s.price_position > 1.3) {
            const targetUC = s.benchmark ? (s.benchmark * nt).toFixed(2) : '—'
            lineItems.push(buildLine(p, s, targetUC, s.spend * (1 - nt / s.price_position)))
          }
        } else {
          lineItems.push(buildLine(p, s, '—', 0))
        }
      })
    })
    lineItems.sort((a, b) => b.spend - a.spend)
  }
  const totalSavings = lineItems.reduce((s, li) => s + (li.savings || 0), 0)

  // ── HTML document ──
  let html = '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0">'
  html += '<title>Data Pack - ' + esc(opp.title) + '</title><style>' + DATAPACK_CSS + '</style></head><body>'
  html += '<button class="print-btn no-print" onclick="window.print()">⬇ Save as PDF</button>'

  // Header with branding
  html += '<div class="header">'
  html += '<div class="logo">SourceOptima<span>Supply Chain Intelligence — ' + (opp.type === 'negotiation' ? 'Negotiation Brief' : 'Data Pack') + '</span></div>'
  html += '<div class="doc-meta"><strong>Classification:</strong> Confidential<br><strong>Generated:</strong> ' + today + '<br><strong>Type:</strong> ' + OT.label + '</div>'
  html += '</div>'

  html += '<h1><span class="badge" style="background:' + OT.color + ';color:' + OT.text + '">' + OT.icon + ' ' + OT.label + '</span> ' + esc(opp.title) + '</h1>'
  html += '<p style="color:#64748b;margin:0 0 16px 0">' + esc(opp.detail) + '</p>'

  // Summary cards
  html += '<div class="summary-grid">'
  html += card('Spend Exposed', formatEur(opp.spendExposed))
  html += card('Estimated Savings', formatRange(opp.estSavings), 'green')
  html += card(opp.type === 'site_gap' ? 'Sites' : 'Parts Affected', opp.type === 'site_gap' ? (opp.vizData?.sites?.length || 0) : fullParts.length)
  html += card('Confidence', opp.confidence)
  html += '</div>'

  // Strategy playbook
  html += '<h2>✨ Recommended Strategy</h2><div class="strategy"><ol>'
  html += strategySteps(opp, fullParts, lineItems).map(s => '<li>' + s + '</li>').join('')
  html += '</ol></div>'
  // Table of Contents (negotiation only)
  if (opp.type === 'negotiation') {
    html += '<div class="toc"><h3>Brief Contents</h3><ol>'
    html += '<li>Exposure Overview \u2014 KPI summary and spend at risk</li>'
    html += '<li>Price Position Analysis \u2014 Prioritized parts with targets</li>'
    html += '<li>Engineering Context \u2014 Manufacturing attributes per part</li>'
    html += '<li>Recommended Strategy \u2014 Algorithm-scored approach</li>'
    html += '<li>Negotiation Talking Points \u2014 Scripts and pushback responses</li>'
    html += '<li>Savings Sensitivity \u2014 What-if scenarios at different targets</li>'
    html += '<li>Per-Part Cohort Evidence \u2014 Full benchmark proof per part</li>'
    html += '<li>Draft Supplier Communication \u2014 Ready-to-send email</li>'
    html += '<li>Volume Commitment Package \u2014 Leverage and counter-offer</li>'
    html += '<li>Alternative Suppliers \u2014 Walk-away plan and RFQ targets</li>'
    html += '<li>Next Steps \u2014 6-week execution timeline</li>'
    html += '</ol></div>'
  }
  // Line items table
  if (opp.type === 'site_gap') {
    html += '<h2>Price by Site (' + lineItems.length + ' sites)</h2>'
    html += '<table><thead><tr><th>#</th><th>Site</th><th>Supplier(s)</th><th class="num">Unit Cost</th><th class="num">Δ vs Best</th><th class="num">Site Spend</th><th class="num target">Savings</th></tr></thead><tbody>'
    lineItems.forEach((li, idx) => {
      html += '<tr' + (li.isBest ? ' style="background:#f0fdf4"' : (Number(li.delta) > 30 ? ' class="highlight"' : '')) + '>'
      html += '<td>' + (idx + 1) + '</td><td><strong>' + esc(li.site) + '</strong></td><td>' + esc(li.suppliers) + '</td>'
      html += '<td class="num">€' + li.uc + '</td><td class="num">' + (li.isBest ? 'best' : '+' + li.delta + '%') + '</td>'
      html += '<td class="num">' + formatEur(li.spend) + '</td><td class="num target">' + formatEur(li.savings) + '</td></tr>'
    })
    html += '<tr style="font-weight:700;background:#f0fdf4"><td colspan="6" style="text-align:right">Total Estimated Savings:</td><td class="num target">' + formatEur(totalSavings) + '</td></tr>'
    html += '</tbody></table>'
  } else if (opp.type === 'part_family') {
    // ── Part Family Execution Pack ──
    const v = opp.vizData || {}
    const bestUC = v.best?.uc || 0
    const partPrices = v.partPrices || []
    const bestLabel = v.best?.desc ? esc(v.best.pn) + ' (' + esc(v.best.desc) + ')' : esc(v.best?.pn || '')

    // ─── STEP 1: Family Map ───
    html += '<h2>Step 1: Family Map</h2>'
    html += '<p style="color:#374151;font-size:11px;margin:4px 0 12px 0">These ' + (v.partCount || 0) + ' parts are grouped because they share the same <strong>manufacturing process</strong>, <strong>material family</strong>, and <strong>size class</strong>. The cohort definition is: <span style="background:#eef2ff;padding:2px 8px;border-radius:4px;font-weight:600;color:#4338ca">' + esc(v.famLabel || '') + '</span></p>'
    html += '<div class="summary-grid" style="grid-template-columns:repeat(5,1fr)">'
    html += card('Parts in Family', v.partCount || 0)
    html += card('Suppliers', v.supplierCount || 0)
    html += card('Total Spend', formatEur(v.totalSpend || 0))
    html += card('Price Range', '\u20AC' + bestUC.toFixed(2) + '\u2013\u20AC' + (v.worst?.uc || 0).toFixed(2))
    html += card('Best-in-Family', '\u20AC' + bestUC.toFixed(2), 'green')
    html += '</div>'

    // ─── STEP 2: Engineering Equivalence ───
    html += '<h2>Step 2: Engineering Equivalence Verification</h2>'
    html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">Each part is scored for similarity to the best-priced reference part (' + bestLabel + '). High similarity (\u226570%) means engineering equivalence is likely \u2014 price differences are negotiable. Low similarity may indicate legitimate engineering differences that justify the premium.</p>'

    // Determine which columns are uniform vs varying
    const specFields = [
      { key: 'method', label: 'Method' },
      { key: 'material', label: 'Material' },
      { key: 'size', label: 'Size Class' },
      { key: 'tolerance_band', label: 'Tolerance Band' },
      { key: 'complexity_tier', label: 'Complexity' },
      { key: 'tightest_mm', label: 'Tightest Tol. (mm)', fmt: v => v != null ? Number(v).toFixed(3) : '\u2014' },
      { key: 'axis_tier', label: 'Axis Tier' },
      { key: 'gauge_band', label: 'Gauge Band' },
    ]
    const specValues = {}
    specFields.forEach(f => { specValues[f.key] = new Set() })
    fullParts.forEach(p => {
      specFields.forEach(f => {
        const val = p[f.key]
        if (val != null && val !== '' && val !== 'unspec' && !String(val).includes('unspec')) specValues[f.key].add(String(val))
      })
    })
    const uniformFields = specFields.filter(f => specValues[f.key].size <= 1)
    const varyingFields = specFields.filter(f => specValues[f.key].size > 1)

    // Find the best-priced part object for similarity comparison
    const refPart = fullParts.find(p => p.pn === v.best?.pn) || fullParts[0]

    // Compute similarity scores for all parts vs reference
    const simScores = fullParts.map(p => ({
      pn: p.pn,
      sim: p === refPart ? { score: 20, pct: 100, reasons: [], diffs: [] } : computeSimilarity(refPart, p),
    }))
    const simMap = {}
    simScores.forEach(s => { simMap[s.pn] = s.sim })

    // Show uniform attributes as a confirmation block
    if (uniformFields.length > 0) {
      html += '<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;padding:10px 14px;margin-bottom:12px;font-size:10px">'
      html += '<strong style="color:#059669">\u2713 Shared attributes (all parts match):</strong> '
      html += uniformFields.map(f => {
        const val = [...specValues[f.key]][0] || 'N/A'
        return '<span style="background:white;padding:1px 6px;border-radius:3px;border:1px solid #d1fae5;margin:0 3px">' + f.label + ': <strong>' + esc(val) + '</strong></span>'
      }).join(' ')
      html += '</div>'
    }
    if (varyingFields.length > 0) {
      html += '<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:6px;padding:10px 14px;margin-bottom:12px;font-size:10px">'
      html += '<strong style="color:#dc2626">\u26A0 Varying attributes (review for price justification):</strong> '
      html += varyingFields.map(f => f.label).join(', ')
      html += '</div>'
    }

    // Full spec comparison table with similarity score
    html += '<table><thead><tr><th>Part Number</th><th>Description</th><th style="background:#eef2ff;color:#4338ca">Similarity</th>'
    specFields.forEach(f => {
      const varies = specValues[f.key].size > 1
      html += '<th style="' + (varies ? 'background:#fef2f2;color:#dc2626' : 'background:#f0fdf4;color:#059669') + '">' + f.label + (varies ? ' \u26A0' : ' \u2713') + '</th>'
    })
    html += '<th class="num">UC</th><th>Supplier</th></tr></thead><tbody>'
    // Sort parts by UC descending to match chart
    const specParts = [...fullParts].sort((a, b) => {
      const aUC = (a.suppliers || []).reduce((max, s) => Math.max(max, s.unit_cost || 0), 0)
      const bUC = (b.suppliers || []).reduce((max, s) => Math.max(max, s.unit_cost || 0), 0)
      return bUC - aUC
    })
    specParts.forEach(p => {
      const mainSup = (p.suppliers || []).sort((a, b) => b.spend - a.spend)[0]
      const uc = mainSup?.unit_cost || 0
      const isBest = uc <= bestUC && uc > 0
      const sim = simMap[p.pn] || { pct: 0, reasons: [], diffs: [] }
      const simColor = sim.pct >= 70 ? '#059669' : sim.pct >= 40 ? '#d97706' : '#dc2626'
      const simBg = sim.pct >= 70 ? '#f0fdf4' : sim.pct >= 40 ? '#fffbeb' : '#fef2f2'
      html += '<tr' + (isBest ? ' style="background:#f0fdf4"' : '') + '>'
      html += '<td><strong>' + esc(p.pn) + '</strong></td><td>' + esc(p.description || '\u2014') + '</td>'
      html += '<td style="background:' + simBg + ';text-align:center"><strong style="color:' + simColor + '">' + (isBest ? 'REF' : sim.pct + '%') + '</strong>'
      if (sim.diffs.length > 0 && !isBest) {
        html += '<div style="font-size:8px;color:#dc2626;margin-top:2px">' + sim.diffs.map(d => d.field).join(', ') + '</div>'
      }
      html += '</td>'
      specFields.forEach(f => {
        const val = f.fmt ? f.fmt(p[f.key]) : (p[f.key] || '\u2014')
        const varies = specValues[f.key].size > 1
        html += '<td' + (varies ? ' style="background:#fef9f9"' : '') + '>' + esc(val) + '</td>'
      })
      html += '<td class="num">\u20AC' + uc.toFixed(2) + '</td>'
      html += '<td>' + esc(mainSup?.name || '\u2014') + '</td></tr>'
    })
    html += '</tbody></table>'

    // Similarity legend
    html += '<div style="margin-top:8px;font-size:9px;color:#64748b">'
    html += '<strong>Similarity legend:</strong> '
    html += '<span style="color:#059669">\u2265 70% = High confidence equivalent</span> \u00B7 '
    html += '<span style="color:#d97706">40\u201369% = Review needed</span> \u00B7 '
    html += '<span style="color:#dc2626">&lt; 40% = Likely different \u2014 price premium may be justified</span>'
    html += '</div>'

    // ─── STEP 3: Price Analysis & Targets ───
    html += '<h2>Step 3: Price Analysis &amp; Targets</h2>'
    html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">Target = best-in-family price (<strong>' + bestLabel + ' at \u20AC' + bestUC.toFixed(2) + '</strong>). Parts are ranked by savings potential. Only challenge parts where engineering equivalence is confirmed in Step 2.</p>'

    const comparison = partPrices.map(p => {
      const gap = bestUC > 0 ? p.uc - bestUC : 0
      const savingsPot = bestUC > 0 && p.uc > bestUC ? p.spend * (1 - bestUC / p.uc) : 0
      const ratio = bestUC > 0 ? (p.uc / bestUC) : 1
      return { ...p, gap, savingsPot, ratio }
    }).sort((a, b) => b.savingsPot - a.savingsPot)
    const famTotalSavings = comparison.reduce((s, c) => s + c.savingsPot, 0)

    html += '<table><thead><tr><th>Priority</th><th>Part Number</th><th>Description</th><th>Supplier</th>'
    html += '<th class="num">Sim.</th>'
    html += '<th class="num">Current UC</th><th class="num">Target UC</th><th class="num">\u0394 Gap</th><th class="num">vs. Best</th>'
    html += '<th class="num">Annual Spend</th><th class="num target">Potential Savings</th></tr></thead><tbody>'
    comparison.forEach((c, idx) => {
      const isBest = c.uc <= bestUC
      const priority = isBest ? 'REFERENCE' : c.savingsPot > 5000 ? 'HIGH' : c.savingsPot > 1000 ? 'MEDIUM' : 'LOW'
      const prioColor = priority === 'HIGH' ? '#dc2626' : priority === 'MEDIUM' ? '#d97706' : priority === 'REFERENCE' ? '#059669' : '#64748b'
      const rowBg = isBest ? ' style="background:#f0fdf4"' : c.savingsPot > 5000 ? ' class="highlight"' : ''
      const sim = simMap[c.pn] || { pct: 0 }
      const simColor = sim.pct >= 70 ? '#059669' : sim.pct >= 40 ? '#d97706' : '#dc2626'
      html += '<tr' + rowBg + '>'
      html += '<td style="color:' + prioColor + ';font-weight:700;font-size:9px">' + priority + '</td>'
      html += '<td><strong>' + esc(c.pn) + '</strong></td><td>' + esc(c.desc || '\u2014') + '</td><td>' + esc(c.supplier) + '</td>'
      html += '<td class="num" style="color:' + simColor + ';font-weight:600">' + (isBest ? 'REF' : sim.pct + '%') + '</td>'
      html += '<td class="num">\u20AC' + c.uc.toFixed(2) + '</td>'
      html += '<td class="num target">\u20AC' + bestUC.toFixed(2) + '</td>'
      html += '<td class="num">' + (isBest ? '\u2014' : '+\u20AC' + c.gap.toFixed(2)) + '</td>'
      html += '<td class="num">' + (isBest ? '1.0\u00D7' : c.ratio.toFixed(1) + '\u00D7') + '</td>'
      html += '<td class="num">' + formatEur(c.spend) + '</td>'
      html += '<td class="num target">' + (c.savingsPot > 0 ? formatEur(c.savingsPot) : '\u2014') + '</td></tr>'
    })
    html += '<tr style="font-weight:700;background:#f0fdf4"><td colspan="9" style="text-align:right">Total Potential Savings (if all aligned to best):</td>'
    html += '<td class="num">' + formatEur(v.totalSpend || 0) + '</td>'
    html += '<td class="num target">' + formatEur(famTotalSavings) + '</td></tr>'
    html += '</tbody></table>'

    // ─── STEP 4: Supplier Landscape ───
    html += '<h2>Step 4: Supplier Landscape</h2>'
    const supBreakdown = v.suppliers || []
    const partsBySup = {}
    fullParts.forEach(p => (p.suppliers || []).forEach(s => { partsBySup[s.name] = (partsBySup[s.name] || 0) + 1 }))
    html += '<table><thead><tr><th>#</th><th>Supplier</th><th class="num">Parts Supplied</th><th class="num">Current Spend</th><th class="num">Share</th><th>Notes</th></tr></thead><tbody>'
    supBreakdown.forEach((s, idx) => {
      const share = v.totalSpend > 0 ? (s.spend / v.totalSpend * 100).toFixed(0) : 0
      const note = idx < 2 ? 'Top supplier \u2014 consolidation candidate' : Number(share) < 5 ? 'Low share \u2014 consider phasing out' : ''
      html += '<tr><td>' + (idx + 1) + '</td><td><strong>' + esc(s.name) + '</strong></td>'
      html += '<td class="num">' + (partsBySup[s.name] || 0) + '</td>'
      html += '<td class="num">' + formatEur(s.spend) + '</td>'
      html += '<td class="num">' + share + '%</td>'
      html += '<td style="font-size:9px;color:#64748b">' + note + '</td></tr>'
    })
    html += '</tbody></table>'

    // ─── Consolidation Analytics (Layer A) ───
    let step = 5
    const cons = opp.consolidation || v.consolidation
    if (cons && v.hasConsolidation) {
      const br = cons.bridge || {}
      const tv = cons.targetVendors || {}
      const fr = cons.fragmentation || {}
      const rk = cons.risk || {}

      // STEP 5: Savings Bridge
      html += '<h2>Step ' + step + ': Consolidation Savings Bridge</h2>'
      html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">Where the consolidation number comes from — built up from four named levers instead of a flat estimate. Each figure is auditable; the per-supplier admin and tooling rates are cited and configurable.</p>'
      html += '<div class="summary-grid" style="grid-template-columns:repeat(4,1fr)">'
      ;(br.components || []).forEach(c => html += card(c.label, formatEur(c.amount), c.amount > 0 ? 'green' : ''))
      html += '</div>'
      html += '<table><thead><tr><th>Lever</th><th class="num">Annual Savings</th><th>Basis</th></tr></thead><tbody>'
      ;(br.components || []).forEach(c => {
        html += '<tr><td><strong>' + esc(c.label) + '</strong></td>'
        html += '<td class="num target">' + (c.amount > 0 ? formatEur(c.amount) : '—') + '</td>'
        html += '<td style="font-size:9px;color:#64748b">' + esc(c.note) + '</td></tr>'
      })
      html += '<tr style="font-weight:700;background:#f0fdf4"><td style="text-align:right">Total economies-of-scale case (range):</td>'
      html += '<td class="num target">' + formatRange(br.total || 0) + '</td>'
      html += '<td style="font-size:9px;color:#64748b">Conservative–likely band, consistent with negotiation savings.</td></tr>'
      html += '</tbody></table>'
      step++

      // STEP 6: Target Vendor Selection
      html += '<h2>Step ' + step + ': Target Vendor Selection</h2>'
      html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">Who should win the family. Incumbents are scored on price position, share of family, breadth of parts already supplied, and whether they hold the best-in-family price. Net-new alternates open competitive tension (3–5 bidders is the sourcing norm).</p>'
      if (tv.anchor) {
        html += '<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px 14px;margin-bottom:12px">'
        html += '<p style="font-weight:700;margin:0 0 4px 0;color:#166534;font-size:12px">✓ Recommended anchor: ' + esc(tv.anchor.name) + ' (score ' + tv.anchor.score + '/100)</p>'
        html += '<p style="margin:0;color:#374151;font-size:10px"><strong>' + esc(tv.anchor.role || 'Incumbent') + '</strong> — ' + esc(tv.anchor.rationale) + '.</p>'
        html += '</div>'
      }
      html += '<table><thead><tr><th>Rank</th><th>Vendor</th><th>Role</th><th class="num">Share</th><th class="num">Parts</th><th class="num">Avg UC</th><th class="num">vs Best</th><th class="num">Score</th></tr></thead><tbody>'
      ;(tv.candidates || []).forEach((c, idx) => {
        const rowBg = idx === 0 ? ' style="background:#f0fdf4"' : ''
        html += '<tr' + rowBg + '><td>' + (idx + 1) + '</td>'
        html += '<td><strong>' + esc(c.name) + '</strong></td>'
        html += '<td style="font-size:9px;color:#64748b">' + esc(c.role || 'Incumbent') + '</td>'
        html += '<td class="num">' + Math.round((c.share || 0) * 100) + '%</td>'
        html += '<td class="num">' + (c.partCount || 0) + '</td>'
        html += '<td class="num">€' + (c.avgUc || 0).toFixed(2) + '</td>'
        html += '<td class="num">' + (c.suppliesBest ? 'best' : (c.ratio || 1).toFixed(1) + '×') + '</td>'
        html += '<td class="num" style="font-weight:600">' + (c.score || 0) + '</td></tr>'
      })
      html += '</tbody></table>'
      if ((tv.netNew || []).length > 0) {
        html += '<p style="color:#374151;font-size:10px;margin:10px 0 4px 0"><strong>Net-new alternates</strong> (from comparable cohort parts — invite to bid for competitive tension):</p>'
        html += '<table><thead><tr><th>Candidate</th><th class="num">Avg Market UC</th><th>Why</th></tr></thead><tbody>'
        tv.netNew.forEach(n => {
          html += '<tr><td><strong>' + esc(n.name) + '</strong></td>'
          html += '<td class="num">' + (n.avgUc > 0 ? '€' + n.avgUc.toFixed(2) : '—') + '</td>'
          html += '<td style="font-size:9px;color:#64748b">' + esc(n.rationale) + '</td></tr>'
        })
        html += '</tbody></table>'
      }
      step++

      // STEP 7: Fragmentation & Single-Source Risk
      html += '<h2>Step ' + step + ': Fragmentation &amp; Single-Source Risk</h2>'
      html += '<div class="summary-grid" style="grid-template-columns:repeat(4,1fr)">'
      html += card('Suppliers', fr.supplierCount || 0)
      html += card('Concentration (HHI)', (fr.hhi || 0) + ' · ' + esc(fr.concentration || ''))
      html += card('Tail Suppliers', (fr.tailSuppliers || []).length + ' (' + formatEur(fr.tailSpend || 0) + ')')
      html += card('Continuity Exposure', formatEur(rk.continuityExposure || 0))
      html += '</div>'
      if ((fr.tailSuppliers || []).length > 0) {
        html += '<p style="color:#374151;font-size:10px;margin:8px 0 4px 0"><strong>Tail-supplier retirement list</strong> (below ' + Math.round((fr.tailShareThreshold || 0.05) * 100) + '% of family spend — phase out after anchor qualification):</p>'
        html += '<table><thead><tr><th>Supplier</th><th class="num">Spend</th><th class="num">Share</th></tr></thead><tbody>'
        fr.tailSuppliers.forEach(s => {
          html += '<tr><td>' + esc(s.name) + '</td><td class="num">' + formatEur(s.spend) + '</td><td class="num">' + Math.round((s.share || 0) * 100) + '%</td></tr>'
        })
        html += '</tbody></table>'
      }
      html += '<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:12px 14px;margin-top:10px;font-size:10px">'
      html += '<p style="font-weight:600;margin:0 0 6px 0;color:#92400e">⚠ Consolidation creates single-source risk — mitigation plan:</p>'
      html += '<ul style="margin:0;padding-left:18px;color:#374151">'
      ;(rk.mitigations || []).forEach(m => html += '<li style="margin:3px 0">' + esc(m) + '</li>')
      html += '</ul></div>'
      step++
    }

    // ─── STEP n: Negotiation Talking Points ───
    html += '<h2>Step ' + step + ': Negotiation Talking Points</h2>'
    html += '<div class="strategy">'
    html += '<p style="font-size:11px;margin:0 0 10px 0"><strong>Opening statement:</strong></p>'
    html += '<blockquote style="border-left:3px solid #6366f1;padding:8px 12px;margin:0 0 12px 0;background:#f8fafc;font-style:italic;font-size:11px">'
    html += '"We purchase ' + (v.partCount || 0) + ' engineering-similar parts in the same process family (' + esc(v.famLabel || '') + '). '
    html += 'Our best achieved price within this family is \u20AC' + bestUC.toFixed(2) + '. '
    html += 'We\u2019d like to understand why certain parts are priced significantly higher and work together to align pricing."'
    html += '</blockquote>'
    html += '<p style="font-size:11px;margin:0 0 8px 0"><strong>Key data points to reference:</strong></p>'
    html += '<ul style="font-size:10px;margin:0;padding-left:18px">'
    html += '<li>Best-in-family: ' + bestLabel + ' at \u20AC' + bestUC.toFixed(2) + '</li>'
    html += '<li>Worst-in-family: ' + esc(v.worst?.pn || '') + ' at \u20AC' + (v.worst?.uc || 0).toFixed(2) + ' (' + Math.round(v.spread || 1) + '\u00D7 ratio)</li>'
    html += '<li>Total family spend: ' + formatEur(v.totalSpend || 0) + ' across ' + (v.supplierCount || 0) + ' suppliers</li>'
    html += '<li>Potential savings if aligned: ' + formatEur(famTotalSavings) + '</li>'
    html += '</ul>'
    html += '<p style="font-size:11px;margin:12px 0 8px 0"><strong>If supplier pushes back:</strong></p>'
    html += '<ul style="font-size:10px;margin:0;padding-left:18px">'
    html += '<li>"What specific engineering differences justify the premium vs. our best-price part?"</li>'
    html += '<li>"If we consolidate the full family volume (' + formatEur(v.totalSpend || 0) + ') with you, what family rate can you offer?"</li>'
    html += '<li>"We need a revised quote within 2 weeks, otherwise we will issue a competitive RFQ for the full family."</li>'
    html += '</ul>'
    html += '</div>'

    // ─── Next Steps ───
    html += '<h2>Next Steps</h2>'
    html += '<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;font-size:10px">'
    html += '<table style="border:none;margin:0"><tbody>'
    html += '<tr><td style="border:none;padding:4px 12px 4px 0;font-weight:600;white-space:nowrap">Week 1\u20132:</td><td style="border:none;padding:4px 0">Verify engineering equivalence (review Step 2 above). Mark any parts with legitimate differences.</td></tr>'
    html += '<tr><td style="border:none;padding:4px 12px 4px 0;font-weight:600;white-space:nowrap">Week 2\u20133:</td><td style="border:none;padding:4px 0">Present data pack to suppliers of HIGH-priority parts. Use talking points from Step 5.</td></tr>'
    html += '<tr><td style="border:none;padding:4px 12px 4px 0;font-weight:600;white-space:nowrap">Week 4:</td><td style="border:none;padding:4px 0">Collect revised quotes. Compare to targets in Step 3.</td></tr>'
    html += '<tr><td style="border:none;padding:4px 12px 4px 0;font-weight:600;white-space:nowrap">Week 5\u20136:</td><td style="border:none;padding:4px 0">If gaps remain, issue competitive RFQ for full family to 2\u20133 alternates.</td></tr>'
    html += '</tbody></table>'
    html += '</div>'
  } else if (opp.type === 'negotiation') {
    // ── Negotiation Execution Pack ──
    const items = opp.vizData?.items || []
    const supplierName = opp.vizData?.supplierName || ''

    // ─── STEP 1: Exposure Overview ───
    html += '<h2>Step 1: Exposure Overview</h2>'
    html += '<p style="color:#374151;font-size:11px;margin:4px 0 12px 0">These ' + items.length + ' parts are all supplied by <strong>' + esc(supplierName) + '</strong> and priced above their cohort benchmark (comparable parts in the same process + material + size class).</p>'
    html += '<div class="summary-grid" style="grid-template-columns:repeat(5,1fr)">'
    html += card('Parts Flagged', items.length)
    html += card('Supplier', esc(supplierName))
    html += card('Total Spend', formatEur(opp.spendExposed))
    html += card('Avg Position', (opp.avgPP || 0).toFixed(2) + 'x')
    html += card('Est. Savings', formatRange(opp.estSavings), 'green')
    html += '</div>'

    // ─── STEP 2: Price Position Analysis ───
    html += '<h2>Step 2: Price Position Analysis</h2>'
    html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">Each part\u2019s unit cost is compared to its cohort benchmark (volume-adjusted median). Price Position = UC \u00F7 Benchmark. Values above 1.3x are flagged. Target: 1.1x (10% premium max).</p>'

    const sorted = [...lineItems].sort((a, b) => b.savings - a.savings)
    html += '<table><thead><tr><th>Priority</th><th>Part Number</th><th>Description</th>'
    html += '<th class="num">Unit Cost</th><th class="num">Benchmark</th><th class="num">Position</th>'
    html += '<th class="num">Target (1.1x)</th><th class="num">Annual Spend</th><th class="num target">Overspend</th></tr></thead><tbody>'
    sorted.forEach((li, idx) => {
      const pp = parseFloat(li.pp) || 0
      const priority = pp > 1.8 ? 'CRITICAL' : pp > 1.5 ? 'HIGH' : 'MEDIUM'
      const prioColor = priority === 'CRITICAL' ? '#7f1d1d' : priority === 'HIGH' ? '#dc2626' : '#d97706'
      const rowBg = pp > 1.5 ? ' class="highlight"' : ''
      html += '<tr' + rowBg + '>'
      html += '<td style="color:' + prioColor + ';font-weight:700;font-size:9px">' + priority + '</td>'
      html += '<td><strong>' + esc(li.pn) + '</strong></td><td>' + esc(li.desc || '\u2014') + '</td>'
      html += '<td class="num">\u20AC' + li.uc + '</td>'
      html += '<td class="num">\u20AC' + li.benchmark + '</td>'
      html += '<td class="num" style="color:' + prioColor + ';font-weight:600">' + li.pp + 'x</td>'
      html += '<td class="num target">\u20AC' + li.targetUC + '</td>'
      html += '<td class="num">' + formatEur(li.spend) + '</td>'
      html += '<td class="num target">' + formatEur(li.savings) + '</td></tr>'
    })
    html += '<tr style="font-weight:700;background:#f0fdf4"><td colspan="7" style="text-align:right">Total Overspend (if all aligned to 1.1x):</td>'
    html += '<td class="num">' + formatEur(opp.spendExposed) + '</td>'
    html += '<td class="num target">' + formatEur(totalSavings) + '</td></tr>'
    html += '</tbody></table>'

    // Competitive items from same supplier
    const goodItems = opp.vizData?.goodItems || []
    if (goodItems.length > 0) {
      const goodSpend = goodItems.reduce((s, g) => s + g.spend, 0)
      html += '<h3 style="color:#059669;margin:16px 0 6px 0;font-size:12px">\u2713 Competitive Parts from Same Supplier (' + goodItems.length + ' items, ' + formatEur(goodSpend) + ' spend)</h3>'
      html += '<p style="color:#374151;font-size:10px;margin:0 0 8px 0">These parts from <strong>' + esc(supplierName) + '</strong> are priced at or below their cohort benchmark \u2014 demonstrating this supplier <em>can</em> price competitively. Use these as evidence in the negotiation.</p>'
      html += '<table><thead><tr><th>Part Number</th><th>Description</th>'
      html += '<th class="num">Unit Cost</th><th class="num">Benchmark</th><th class="num">Position</th><th class="num">EAU</th><th class="num">Annual Spend</th></tr></thead><tbody>'
      goodItems.forEach(g => {
        html += '<tr style="background:#f0fdf4">'
        html += '<td><strong>' + esc(g.pn) + '</strong></td><td>' + esc(g.desc || '\u2014') + '</td>'
        html += '<td class="num">\u20AC' + g.uc.toFixed(2) + '</td>'
        html += '<td class="num">\u20AC' + (g.bm > 0 ? g.bm.toFixed(2) : '\u2014') + '</td>'
        html += '<td class="num" style="color:#059669;font-weight:600">' + g.pp.toFixed(2) + 'x</td>'
        html += '<td class="num">' + (g.eau > 0 ? Math.round(g.eau).toLocaleString() : '\u2014') + '</td>'
        html += '<td class="num">' + formatEur(g.spend) + '</td></tr>'
      })
      html += '</tbody></table>'
    }

    // ─── STEP 3: Engineering Context ───
    html += '<h2>Step 3: Engineering Context</h2>'
    html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">Manufacturing attributes for each part \u2014 confirm parts are genuinely comparable within their cohorts before challenging pricing.</p>'
    html += '<table><thead><tr><th>Part Number</th><th>Material</th><th>Method</th><th>Tolerance</th><th>Complexity</th><th>Size</th><th style="font-size:8px">Sourcing Cohort</th></tr></thead><tbody>'
    fullParts.forEach(p => {
      html += '<tr><td><strong>' + esc(p.pn) + '</strong></td>'
      html += '<td>' + esc(p.material || '\u2014') + '</td><td>' + esc(p.method || '\u2014') + '</td>'
      html += '<td>' + esc(p.tolerance_band || '\u2014') + '</td><td>' + esc(p.complexity_tier || '\u2014') + '</td>'
      html += '<td>' + esc(p.size || '\u2014') + '</td>'
      html += '<td style="font-size:8px">' + esc(humanizeCohortName(p.sourcing_cohort)) + '</td></tr>'
    })
    html += '</tbody></table>'

    // ─── STEP 4: Recommended Strategy ───
    html += '<h2>Step 4: Recommended Negotiation Strategy</h2>'
    const strategies = opp.strategies || []
    if (strategies.length > 0) {
      const top = strategies[0]
      const viable = strategies.filter(s => s.score >= 50)
      const notRec = strategies.filter(s => s.score < 30)
      html += '<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:14px;margin-bottom:12px">'
      html += '<p style="font-weight:700;margin:0 0 6px 0;color:#166534;font-size:12px">' + top.icon + ' Primary: ' + esc(top.name) + ' (score ' + top.score + '/100)</p>'
      html += '<p style="margin:0 0 10px 0;color:#374151;font-size:10px">' + esc(top.desc) + '</p>'
      if (viable.length > 1) {
        html += '<p style="font-weight:600;margin:0 0 4px 0;color:#374151;font-size:10px">Also viable:</p>'
        html += '<ul style="margin:0 0 8px 0;padding-left:18px;font-size:10px">'
        viable.slice(1, 4).forEach(s => {
          html += '<li><strong>' + esc(s.name) + '</strong> (' + s.score + '/100) \u2014 ' + esc(s.desc) + '</li>'
        })
        html += '</ul>'
      }
      if (notRec.length > 0) {
        html += '<p style="margin:0;color:#9ca3af;font-size:9px">Not recommended for this supplier: ' + notRec.map(s => esc(s.name)).join(', ') + '.</p>'
      }
      html += '</div>'
      // Score breakdown table
      html += '<table><thead><tr><th>Strategy</th><th class="num">Score</th><th>Key Signal</th></tr></thead><tbody>'
      strategies.forEach(s => {
        const bg = s.score >= 70 ? '#f0fdf4' : s.score >= 50 ? '#fffbeb' : '#fef2f2'
        const label = s.score >= 70 ? 'Recommended' : s.score >= 50 ? 'Viable' : s.score >= 30 ? 'Consider' : 'Not recommended'
        html += '<tr style="background:' + bg + '"><td>' + s.icon + ' ' + esc(s.name) + '</td>'
        html += '<td class="num" style="font-weight:600">' + s.score + '</td>'
        html += '<td style="font-size:9px;color:#6b7280">' + label + '</td></tr>'
      })
      html += '</tbody></table>'
    } else {
      html += '<p style="color:#6b7280;font-size:10px">Strategy scoring requires cohort data. Run price position analysis first.</p>'
    }

    // ─── STEP 5: Negotiation Script ───
    html += '<h2>Step 5: Negotiation Talking Points</h2>'
    html += '<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:14px;font-size:10px">'
    html += '<p style="font-weight:600;margin:0 0 8px 0;color:#991b1b">Opening Statement:</p>'
    html += '<p style="margin:0 0 12px 0;font-style:italic">&ldquo;Our benchmarking analysis shows ' + items.length + ' parts priced ' + Math.round(((opp.avgPP || 1) - 1) * 100) + '% above comparable market rates. We value the relationship and want to resolve this collaboratively. Here is the data.&rdquo;</p>'
    html += '<p style="font-weight:600;margin:0 0 8px 0;color:#991b1b">Key Data Points:</p>'
    html += '<ul style="margin:0;padding-left:18px">'
    html += '<li>' + items.length + ' line items above 1.3x benchmark</li>'
    const critCount = items.filter(i => i.pp > 1.5).length
    if (critCount > 0) html += '<li>' + critCount + ' items exceed 1.5x \u2014 these are significant outliers requiring immediate attention</li>'
    html += '<li>Total annual overspend: ' + formatEur(totalSavings) + '</li>'
    html += '<li>Our target: 1.1x benchmark (10% premium to account for service/quality factors)</li>'
    if (goodItems.length > 0) html += '<li>You are competitive on ' + goodItems.length + ' other parts (' + formatEur(goodItems.reduce((s, g) => s + g.spend, 0)) + ' spend) \u2014 we know you can do this</li>'
    html += '</ul>'
    html += '<p style="font-weight:600;margin:12px 0 8px 0;color:#991b1b">If supplier pushes back:</p>'
    html += '<ul style="margin:0;padding-left:18px">'
    html += '<li>&ldquo;What specific factors justify pricing ' + Math.round(((opp.avgPP || 1) - 1) * 100) + '% above comparable parts in your peer group?&rdquo;</li>'
    if (goodItems.length > 0) html += '<li>&ldquo;You price ' + goodItems.length + ' other parts at benchmark or below. Why are these ' + items.length + ' items different?&rdquo;</li>'
    html += '<li>&ldquo;We are prepared to consolidate our ' + formatEur(opp.spendExposed) + ' spend if pricing aligns. Otherwise, we will be issuing RFQs to qualified alternates.&rdquo;</li>'
    html += '<li>&ldquo;We need revised pricing within 2 weeks. After that, our procurement team will proceed with competitive sourcing.&rdquo;</li>'
    html += '</ul>'
    html += '</div>'

    // ─── STEP 6: Sensitivity Analysis ───
    html += '<div class="page-break"></div>'
    html += '<h2>Step 6: Savings Sensitivity Analysis</h2>'
    html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">What savings look like at different negotiated outcomes. Use this to set your walk-in target, acceptable middle-ground, and walk-away floor.</p>'
    html += '<table><thead><tr><th>Target Level</th><th>Premium over Benchmark</th><th class="num">Total Savings</th><th class="num">Savings %</th><th>Assessment</th></tr></thead><tbody>'
    ;[{m:1.0, lbl:'At benchmark'}, {m:1.05, lbl:'5% premium'}, {m:1.1, lbl:'10% premium'}, {m:1.15, lbl:'15% premium'}, {m:1.2, lbl:'20% premium'}, {m:1.3, lbl:'30% premium (walk-away)'}].forEach(row => {
      const sav = items.reduce((s, i) => {
        if (!i.bm || i.bm <= 0) return s
        const t = i.bm * row.m
        return t >= i.uc ? s : s + i.spend * (1 - t / i.uc)
      }, 0)
      const pct = opp.spendExposed > 0 ? Math.round(sav / opp.spendExposed * 100) : 0
      const assess = row.m <= nt ? 'Stretch goal' : row.m <= nt + 0.05 ? 'Good outcome' : row.m <= nt + 0.1 ? 'Acceptable' : 'Floor'
      const bg = row.m === nt ? ' style="background:#f0fdf4;font-weight:600"' : row.m === 1.3 ? ' style="background:#fef2f2"' : ''
      html += '<tr' + bg + '><td>' + esc(row.lbl) + '</td><td>+' + Math.round((row.m - 1) * 100) + '%</td>'
      html += '<td class="num">' + formatEur(sav) + '</td><td class="num">' + pct + '%</td><td>' + assess + '</td></tr>'
    })
    html += '</tbody></table>'

    // ─── STEP 7: Per-Part Cohort Breakdown ───
    html += '<div class="page-break"></div>'
    html += '<h2>Step 7: Per-Part Cohort Evidence</h2>'
    html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">For each overpriced part, the full cohort is shown \u2014 comparable parts from the market that establish the benchmark. This is your proof that the benchmark is real.</p>'
    items.slice(0, 8).forEach(item => {
      const p = fullParts.find(pt => pt.pn === item.pn)
      const sup = p?.suppliers?.find(s => s.name === supplierName)
      if (!sup?.cohort_entries?.length) return
      html += '<h3 style="margin:16px 0 4px 0;font-size:11px;color:#1f2937">' + esc(item.pn) + (item.desc ? ' \u2014 ' + esc(item.desc) : '') + '</h3>'
      html += '<div style="display:flex;gap:16px;flex-wrap:wrap;font-size:9px;color:#6b7280;margin-bottom:6px">'
      html += '<span>UC: <strong>\u20AC' + item.uc.toFixed(2) + '</strong></span>'
      html += '<span>BM: <strong>\u20AC' + item.bm.toFixed(2) + '</strong></span>'
      html += '<span>PP: <strong style="color:#dc2626">' + item.pp.toFixed(2) + 'x</strong></span>'
      html += '<span>Target: <strong style="color:#059669">\u20AC' + (item.bm * nt).toFixed(2) + '</strong></span>'
      html += '<span>Cohort: ' + esc(humanizeCohortName(sup.cohort_name)) + ' (' + (sup.cohort_size || sup.cohort_entries.length) + ' members)</span>'
      html += '</div>'
      html += '<table style="font-size:9px"><thead><tr><th>Part</th><th>Supplier</th><th class="num">Unit Cost</th><th class="num">EAU</th><th>This supplier?</th></tr></thead><tbody>'
      sup.cohort_entries.sort((a, b) => a.unit_cost - b.unit_cost).forEach(e => {
        const isThis = e.supplier === supplierName
        html += '<tr style="' + (isThis ? 'background:#fef2f2;font-weight:600' : '') + '">'
        html += '<td>' + esc(e.pn || '\u2014') + '</td><td>' + esc(e.supplier || '\u2014') + '</td>'
        html += '<td class="num">\u20AC' + e.unit_cost.toFixed(2) + '</td>'
        html += '<td class="num">' + (e.eau > 0 ? Math.round(e.eau).toLocaleString() : '\u2014') + '</td>'
        html += '<td>' + (isThis ? '\u2713 Yes' : '') + '</td></tr>'
      })
      html += '</tbody></table>'
    })

    // ─── STEP 8: Draft Supplier Communication ───
    html += '<div class="page-break"></div>'
    html += '<h2>Step 8: Draft Supplier Communication</h2>'
    html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">Ready-to-use email template. Copy and customize for your specific context.</p>'
    html += '<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:16px;font-size:10px;font-family:sans-serif;line-height:1.6;white-space:pre-wrap">'
    html += '<strong>Subject:</strong> Pricing Review Request \u2014 ' + items.length + ' Line Items\n\n'
    html += 'Dear ' + esc(supplierName) + ' Team,\n\n'
    html += 'As part of our ongoing supply chain optimization, we have completed a benchmarking analysis of our portfolio with your company.\n\n'
    html += 'Our analysis shows <strong>' + items.length + ' parts priced ' + Math.round(((opp.avgPP || 1) - 1) * 100) + '% above comparable market rates</strong> (' + formatEur(opp.spendExposed) + ' annual spend). We have detailed benchmarking data for each line item and would like to discuss alignment.\n\n'
    if (goodItems.length > 0) {
      html += 'We note that ' + goodItems.length + ' other items in our portfolio with you are priced competitively \u2014 we value the relationship and want to resolve this collaboratively.\n\n'
    }
    html += 'We propose:\n'
    html += '1. Review the attached price position analysis\n'
    html += '2. Provide revised pricing for highlighted items within 2 weeks\n'
    html += '3. Schedule a brief call to discuss any items requiring clarification\n\n'
    html += 'Our target is ' + formatEur(items.reduce((s, i) => i.bm > 0 ? s + i.spend * (1 - (i.bm * nt) / i.uc) : s, 0)) + ' annual savings, which we believe is achievable while maintaining fair margins on your side.\n\n'
    html += 'Please let us know your availability for a brief discussion.\n\n'
    html += 'Best regards'
    html += '</div>'

    // ─── STEP 9: Volume Commitment Package ───
    html += '<h2>Step 9: Volume Commitment Package</h2>'
    html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">Your leverage: the full relationship value you bring. Present this as the reward for alignment.</p>'
    const goodSpendTotal = goodItems.reduce((s, g) => s + g.spend, 0)
    const totalRelationship = opp.spendExposed + goodSpendTotal
    html += '<table><thead><tr><th>Category</th><th class="num">Parts</th><th class="num">Annual Spend</th><th class="num">% of Relationship</th></tr></thead><tbody>'
    html += '<tr><td>Overpriced parts (negotiation target)</td><td class="num">' + items.length + '</td><td class="num">' + formatEur(opp.spendExposed) + '</td><td class="num">' + Math.round(opp.spendExposed / totalRelationship * 100) + '%</td></tr>'
    html += '<tr style="background:#f0fdf4"><td>Competitive parts (retention commitment)</td><td class="num">' + goodItems.length + '</td><td class="num">' + formatEur(goodSpendTotal) + '</td><td class="num">' + Math.round(goodSpendTotal / totalRelationship * 100) + '%</td></tr>'
    html += '<tr style="font-weight:700"><td>Total supplier relationship</td><td class="num">' + (items.length + goodItems.length) + '</td><td class="num">' + formatEur(totalRelationship) + '</td><td class="num">100%</td></tr>'
    html += '</tbody></table>'
    html += '<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px;margin-top:10px;font-size:10px">'
    html += '<p style="font-weight:600;margin:0 0 4px 0;color:#166534">Your offer:</p>'
    html += '<p style="margin:0 0 4px 0">"If you align the ' + items.length + ' overpriced parts to 1.1x benchmark, we commit the full ' + formatEur(totalRelationship) + ' annual portfolio for a 2\u20133 year term."</p>'
    html += '<p style="font-weight:600;margin:8px 0 4px 0;color:#991b1b">Walk-away:</p>'
    html += '<p style="margin:0">"If pricing does not align within 2 weeks, the ' + formatEur(opp.spendExposed) + ' in overpriced spend goes to competitive RFQ."</p>'
    html += '</div>'

    // ─── STEP 10: Alternative Suppliers ───
    html += '<h2>Step 10: Alternative Suppliers (Walk-Away Plan)</h2>'
    html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">Suppliers identified from cohort benchmarking data who serve comparable parts. These are your RFQ targets if negotiations fail.</p>'
    const altSuppliers = new Set()
    items.forEach(item => {
      const p = fullParts.find(pt => pt.pn === item.pn)
      const sup = p?.suppliers?.find(s => s.name === supplierName)
      ;(sup?.cohort_entries || []).forEach(e => {
        if (e.supplier && e.supplier !== supplierName) altSuppliers.add(e.supplier)
      })
    })
    if (altSuppliers.size > 0) {
      html += '<table><thead><tr><th>Alternative Supplier</th><th>Source</th></tr></thead><tbody>'
      ;[...altSuppliers].slice(0, 15).forEach(name => {
        html += '<tr><td><strong>' + esc(name) + '</strong></td><td>Identified in cohort benchmarking</td></tr>'
      })
      html += '</tbody></table>'
      html += '<p style="color:#6b7280;font-size:9px;margin-top:6px">' + altSuppliers.size + ' alternative supplier' + (altSuppliers.size !== 1 ? 's' : '') + ' identified. Timeline: 2-week RFQ + 4-week qualification = parts can move in ~6 weeks for commodity items, ~12 weeks for complex parts.</p>'
    } else {
      html += '<p style="color:#6b7280;font-size:10px">No alternative suppliers identified in current cohort data. Consider broadening the search to adjacent material/process families.</p>'
    }

    // ─── STEP 11: Execution Timeline ───
    html += '<div class="page-break"></div>'
    html += '<h2>Step 11: Execution Timeline</h2>'
    html += '<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;font-size:10px">'
    html += '<table style="border:none;margin:0"><tbody>'
    html += '<tr><td style="border:none;padding:6px 12px 6px 0;font-weight:600;white-space:nowrap;vertical-align:top">Week 1:</td><td style="border:none;padding:6px 0">Internal prep. Review Steps 3\u20134 (engineering context, strategy). Confirm cohort assignments are correct. Identify any parts that may have legitimate complexity differences.</td></tr>'
    html += '<tr><td style="border:none;padding:6px 12px 6px 0;font-weight:600;white-space:nowrap;vertical-align:top">Week 2:</td><td style="border:none;padding:6px 0">Send email (Step 8) to ' + esc(supplierName) + '. Attach this playbook as PDF. Request meeting.</td></tr>'
    html += '<tr><td style="border:none;padding:6px 12px 6px 0;font-weight:600;white-space:nowrap;vertical-align:top">Week 3:</td><td style="border:none;padding:6px 0">QBR / negotiation meeting. Present Steps 1\u20132 (exposure + positions). Use Step 5 talking points. Present volume commitment (Step 9).</td></tr>'
    html += '<tr><td style="border:none;padding:6px 12px 6px 0;font-weight:600;white-space:nowrap;vertical-align:top">Week 4:</td><td style="border:none;padding:6px 0">Collect revised pricing. Compare to sensitivity analysis (Step 6). Accept anything in \u201cGood outcome\u201d or better range.</td></tr>'
    html += '<tr><td style="border:none;padding:6px 12px 6px 0;font-weight:600;white-space:nowrap;vertical-align:top">Week 5\u20136:</td><td style="border:none;padding:6px 0">For remaining gaps: issue competitive RFQ to alternative suppliers (Step 10) for CRITICAL/HIGH priority items. Inform supplier of live competition.</td></tr>'
    html += '<tr><td style="border:none;padding:6px 12px 6px 0;font-weight:600;white-space:nowrap;vertical-align:top">Week 7\u20138:</td><td style="border:none;padding:6px 0">Evaluate RFQ responses. Award or return to supplier with competitive quotes as final leverage.</td></tr>'
    html += '</tbody></table>'
    html += '</div>'

    // ─── Drawing References ───
    const partsWithDrawings = fullParts.filter(p => p.file_id || p.filename)
    if (partsWithDrawings.length > 0) {
      html += '<div class="page-break"></div>'
      html += '<h2>Appendix A: Engineering Drawing References</h2>'
      html += '<p style="color:#374151;font-size:10px;margin:4px 0 8px 0">CAD drawings on file for parts in this negotiation. Access via the SourceOptima document management system.</p>'
      html += '<table><thead><tr><th>Part Number</th><th>Description</th><th>Drawing File</th><th>File ID</th></tr></thead><tbody>'
      partsWithDrawings.forEach(p => {
        html += '<tr><td><strong>' + esc(p.pn) + '</strong></td><td>' + esc(p.description || '\u2014') + '</td>'
        html += '<td>' + esc(p.filename || '\u2014') + '</td><td style="font-size:8px;color:#6b7280">' + esc(p.file_id || '\u2014') + '</td></tr>'
      })
      html += '</tbody></table>'
    }

  } else {
    html += '<h2>Detailed Line Items (' + lineItems.length + ' rows)</h2>'
    html += '<table><thead><tr><th>#</th><th>Part Number</th><th>Description</th><th>Supplier</th><th>Material</th><th>Method</th><th>Tolerance</th><th>Complexity</th>'
    html += '<th class="num">Unit Cost</th><th class="num">EAU</th><th class="num">Benchmark</th><th class="num">Price Pos.</th><th class="num">Spend</th>'
    if (opp.type === 'negotiation') html += '<th class="num target">Target UC</th><th class="num target">Savings</th>'
    html += '</tr></thead><tbody>'
    lineItems.forEach((li, idx) => {
      const cls = parseFloat(li.pp) > 1.5 ? ' class="highlight"' : ''
      html += '<tr' + cls + '><td>' + (idx + 1) + '</td><td><strong>' + esc(li.pn) + '</strong></td><td>' + esc(li.desc) + '</td><td>' + esc(li.supplier) + '</td>'
      html += '<td>' + esc(li.material) + '</td><td>' + esc(li.method) + '</td><td>' + esc(li.tolerance) + '</td><td>' + esc(li.complexity) + '</td>'
      html += '<td class="num">€' + li.uc + '</td><td class="num">' + esc(li.eau) + '</td><td class="num">€' + li.benchmark + '</td>'
      html += '<td class="num"><strong>' + li.pp + 'x</strong></td><td class="num">' + formatEur(li.spend) + '</td>'
      if (opp.type === 'negotiation') html += '<td class="num target">€' + li.targetUC + '</td><td class="num target">' + formatEur(li.savings) + '</td>'
      html += '</tr>'
    })
    if (opp.type === 'negotiation' && totalSavings > 0) {
      html += '<tr style="font-weight:700;background:#f0fdf4"><td colspan="14" style="text-align:right">Total Estimated Savings:</td><td class="num target">' + formatEur(totalSavings) + '</td></tr>'
    }
    html += '</tbody></table>'

    // Extended part specifications
    html += '<h2>Part Specifications (Extended Detail)</h2>'
    html += '<table><thead><tr><th>Part Number</th><th>Description</th><th>Process Family</th><th>Material</th><th>Method</th>'
    html += '<th class="num">Tolerance (mm)</th><th>Tolerance Band</th><th>Axis Tier</th><th>Gauge Band</th><th>Complexity</th><th>Size</th><th>Sourcing Cohort</th></tr></thead><tbody>'
    fullParts.forEach(p => {
      html += '<tr><td><strong>' + esc(p.pn) + '</strong></td><td>' + esc(p.description || '—') + '</td><td>' + esc(p.process_family || '—') + '</td>'
      html += '<td>' + esc(p.material || '—') + '</td><td>' + esc(p.method || '—') + '</td>'
      html += '<td class="num">' + (p.tightest_mm != null ? p.tightest_mm : '—') + '</td><td>' + esc(p.tolerance_band || '—') + '</td>'
      html += '<td>' + esc(p.axis_tier || '—') + '</td><td>' + esc(p.gauge_band || '—') + '</td><td>' + esc(p.complexity_tier || '—') + '</td>'
      html += '<td>' + esc(p.size || '—') + '</td><td style="font-size:8px">' + esc(humanizeCohortName(p.sourcing_cohort)) + '</td></tr>'
    })
    html += '</tbody></table>'
  }

  html += '<div class="footer"><span>Generated by SourceOptima — Supply Chain Intelligence Platform</span><span>Confidential — ' + today + '</span></div>'
  html += '</body></html>'

  const w = window.open('', '_blank')
  if (w) { w.document.write(html); w.document.close() }
  else alert('Please allow pop-ups to generate the Data Pack.')
}

function buildLine(p, s, targetUC, savings) {
  return {
    pn: p.pn, desc: p.description || '', material: p.material || '—',
    tolerance: p.tolerance_band || '—', complexity: p.complexity_tier || '—',
    method: p.method || '—', size: p.size || '—', family: p.family || '—',
    supplier: s.name, uc: Number(s.unit_cost).toFixed(2), eau: s.eau || '—',
    benchmark: s.benchmark ? s.benchmark.toFixed(2) : '—',
    pp: s.price_position ? s.price_position.toFixed(2) : '—',
    spend: s.spend, targetUC, savings,
  }
}

function card(label, value, cls) {
  return '<div class="summary-card"><div class="label">' + label + '</div><div class="value' + (cls ? ' ' + cls : '') + '">' + value + '</div></div>'
}

function strategySteps(opp, fullParts, lineItems) {
  if (opp.type === 'negotiation') {
    const items = opp.vizData?.items || []
    const goodItems = opp.vizData?.goodItems || []
    const critCount = items.filter(i => i.pp > 1.5).length
    const topItems = lineItems.slice(0, 3).map(li => li.desc ? esc(li.pn) + ' (' + esc(li.desc) + ')' : esc(li.pn)).join(', ')
    const goodNote = goodItems.length > 0 ? ' Supplier is competitive on ' + goodItems.length + ' other parts \u2014 use as leverage.' : ''
    const stratNote = opp.strategies && opp.strategies.length > 0 ? ' Recommended strategy: ' + opp.strategies[0].name + ' (' + opp.strategies[0].score + '/100).' : ''
    return [
      '<strong>Exposure:</strong> ' + items.length + ' parts from ' + esc(opp.vizData?.supplierName || '') + ' priced ' + Math.round(((opp.avgPP || 1) - 1) * 100) + '% above benchmark on average. Total spend: ' + formatEur(opp.spendExposed) + '.' + (critCount > 0 ? ' <span style="color:#dc2626">' + critCount + ' critical outliers (&gt;1.5x).</span>' : '') + goodNote + stratNote,
      '<strong>Approach:</strong> Present benchmarking data (Step 2). Lead with highest-overspend items: ' + topItems + '. Target: 1.1x benchmark max.',
      '<strong>Target:</strong> ' + formatRange(opp.estSavings) + ' annual savings. Execute over 4\u20136 weeks via QBR + 2-week response deadline.',
    ]
  }
  if (opp.type === 'sole_source') {
    return [
      '<strong>Risk Assessment:</strong> Single-source exposure across ' + fullParts.length + ' parts. No competitive pressure on pricing.',
      '<strong>Dual-Source Strategy:</strong> Qualify alternate supplier for top 3—5 highest-spend parts to create competitive tension.',
      '<strong>Leverage Point:</strong> Use qualification effort as leverage in current supplier pricing discussions.',
      '<strong>Target Outcome:</strong> Either 5–10% price improvement from incumbent or qualified alternate within 6 months.',
    ]
  }
  if (opp.type === 'part_family') {
    const v = opp.vizData || {}
    const bestUC = v.best?.uc || 0
    const famTotalSavings = (v.partPrices || []).reduce((s, p) => {
      return s + (bestUC > 0 && p.uc > bestUC ? p.spend * (1 - bestUC / p.uc) : 0)
    }, 0)
    // Similarity summary for exec bullets
    const refPart = (fullParts || []).find(p => p.pn === v.best?.pn)
    let simNote = ''
    if (refPart) {
      const others = (fullParts || []).filter(p => p !== refPart)
      const highSim = others.filter(p => computeSimilarity(refPart, p).pct >= 70).length
      if (highSim > 0) simNote = ' Similarity analysis confirms ' + highSim + ' of ' + others.length + ' parts are \u226570% similar to the reference \u2014 strong negotiation ground.'
    }
    return [
      '<strong>Opportunity:</strong> ' + (v.partCount || 0) + ' engineering-similar parts (' + esc(v.famLabel || '') + ') with prices ranging \u20AC' + bestUC.toFixed(2) + '\u2013\u20AC' + (v.worst?.uc || 0).toFixed(2) + ' (' + Math.round(v.spread || 1) + '\u00D7). Total family spend: ' + formatEur(v.totalSpend || 0) + '.',
      '<strong>Approach:</strong> Verify engineering equivalence (Step 2), then use best-in-family price as negotiation anchor for all higher-priced parts (Step 3).' + simNote + (v.hasConsolidation ? ' Consolidate volume across ' + (v.supplierCount || 0) + ' suppliers for additional leverage (Step 4).' : ''),
      '<strong>Target:</strong> ' + formatRange(opp.estSavings) + ' annual savings (' + formatEur(famTotalSavings) + ' if fully aligned to best). Execute over 4\u20136 weeks.',
    ]
  }
  if (opp.type === 'site_gap') {
    const v = opp.vizData || {}
    const worst = (v.sites || [])[0]
    return [
      '<strong>Confirm the same part:</strong> ' + esc(v.pn) + (v.desc ? ' (' + esc(v.desc) + ')' : '') + ' is purchased at ' + (v.sites?.length || 0) + ' sites. Verify identical spec/revision before harmonizing.',
      '<strong>Internal benchmark:</strong> Best in-network price is €' + (v.best || 0).toFixed(2) + '/unit at ' + esc(v.bestSite) + ' — already achieved, so no negotiation is needed to prove it is attainable.',
      '<strong>Tackle the worst gap first:</strong> ' + (worst ? esc(worst.site) + ' pays €' + worst.unit_cost.toFixed(2) + ' (+' + Math.round((worst.unit_cost / v.best - 1) * 100) + '%)' : '') + '. Greatest savings per unit of effort.',
      '<strong>Harmonize contracts:</strong> Extend the best site’s price/terms network-wide, or consolidate all sites’ volume under one agreement.',
      '<strong>Target Outcome:</strong> Pull every site to best-in-network pricing — ' + formatRange(opp.estSavings) + ' annual savings, no requalification.',
    ]
  }
  return [
    '<strong>Quick Win:</strong> Known lower-cost alternative exists with comparable specifications.',
    '<strong>Action:</strong> Initiate qualification of alternative supplier. Present comparative pricing to current supplier.',
    '<strong>Target Outcome:</strong> Immediate cost reduction via supplier switch or incumbent price match.',
  ]
}

const DATAPACK_CSS = `
body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:#1a1a2e;margin:0;padding:40px;font-size:11px;line-height:1.5}
@page{size:A4 landscape;margin:15mm 12mm}
@media print{body{padding:0}.no-print{display:none!important}.page-break{page-break-before:always}}
.page-break{page-break-before:always;margin-top:32px}
.header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1e5045;padding-bottom:16px;margin-bottom:24px}
.logo{font-size:22px;font-weight:700;color:#1e5045;letter-spacing:-0.5px}
.logo span{color:#64748b;font-weight:400;font-size:13px;display:block}
.doc-meta{text-align:right;font-size:10px;color:#64748b}
.doc-meta strong{color:#1a1a2e}
h1{font-size:18px;margin:0 0 8px 0;color:#1a1a2e}
h2{font-size:14px;margin:28px 0 10px 0;color:#1e5045;border-bottom:1px solid #e2e8f0;padding-bottom:4px}
h3{font-size:11px;margin:14px 0 6px 0;color:#374151}
.badge{display:inline-block;padding:2px 10px;border-radius:4px;font-size:10px;font-weight:600}
.summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:16px 0}
.summary-card{background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:12px}
.summary-card .label{font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px}
.summary-card .value{font-size:16px;font-weight:700;color:#1a1a2e;margin-top:4px}
.summary-card .value.green{color:#059669}
.toc{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:16px;margin:16px 0}
.toc h3{margin:0 0 8px 0;font-size:12px;color:#1e5045}
.toc ol{margin:0;padding-left:20px;font-size:10px;line-height:2}
.toc a{color:#1e5045;text-decoration:none}
.toc a:hover{text-decoration:underline}
table{width:100%;border-collapse:collapse;font-size:9px;margin:12px 0;page-break-inside:auto}
thead{background:#f1f5f9}
th{padding:5px 6px;text-align:left;font-weight:600;border-bottom:2px solid #e2e8f0;white-space:nowrap}
td{padding:4px 6px;border-bottom:1px solid #f1f5f9}
tr:nth-child(even){background:#fafbfc}
.num{text-align:right;font-family:"SF Mono",Menlo,monospace}
.highlight{background:#fef2f2!important}
.target{color:#059669;font-weight:600}
.strategy{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:16px;margin:16px 0}
.strategy ol{margin:8px 0;padding-left:20px}
.strategy li{margin:6px 0;font-size:11px}
.footer{margin-top:32px;padding-top:12px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:9px;color:#94a3b8}
.print-btn{position:fixed;top:20px;right:20px;background:#1e5045;color:white;border:none;padding:12px 24px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,0.15);z-index:999}
.print-btn:hover{background:#0d5d57}
@media(max-width:768px){body{padding:16px;font-size:10px}.header{flex-direction:column;gap:8px}.summary-grid{grid-template-columns:repeat(2,1fr);gap:8px}.summary-card .value{font-size:14px}h1{font-size:15px}h2{font-size:13px}.strategy{padding:12px}.print-btn{top:10px;right:10px;padding:8px 14px;font-size:12px}}
@media(max-width:480px){.summary-grid{grid-template-columns:1fr}table{font-size:8px}th,td{padding:3px 4px}.strategy ol{padding-left:14px}.strategy li{font-size:10px}}
`
